import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useCookie } from "@core/composable/useCookie"
import * as Sentry from "@sentry/vue"
import { useI18n } from "vue-i18n"


const email = import.meta.env.VITE_BACKEND_TEST_USER_EMAIL
const password = import.meta.env.VITE_BACKEND_TEST_USER_PASSWORD
const basicAuth = import.meta.env.VITE_BACKEND_TEST_BASIC_AUTH // Basic Auth base64 encoded string
const baseUrl = import.meta.env.VITE_BACKEND_SERVER_URL

export const useUserStore = defineStore('userStore', () => {

  const isLoading = ref(false)

  const { t } = useI18n()

  // 👉 Support page data object
  const supportPageInfo = ref({
    isLoaded: false,
    consultantPhone: '',
    activeSalonerProductsAlert: false,
  })

  // 👉 Ambassador page data object
  const ambassadorPageInfo = ref({
    isLoaded: false,
    isAmbassador: false,
    content: {},
  })

  // 👉 Statistics page data object
  const statisticsPageInfo = ref({
    isLoaded: false,
    consultantPhone: '',
    activeSalonerProductsAlert: false,
  })

  // 👉 Blank user object template
  const blankUser = ref({
    employeeId: '', //id
    firstname: '',
    lastname: '',
    mail: '',
    phone: '',
  })

  // 👉 Current user object
  const currentUser = ref({})

  // 👉 Login and fetch access token // moved from backend.js
  const fetchAccessTokenByEmail = async () => {
    isLoading.value = true
    try {
      const response = await fetch(`${baseUrl}/auth/login`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${basicAuth}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      })

      if (response.ok) {
        const data = await response.json()

        if (data.user) {
          setUser(data.user)
        } else {
          clearUser()
        }

        return data.access_token || null
      } else {
        console.error('Login request failed', response.status, await response.text())

        return null
      }
    } catch (error) {
      console.error('Error during login request:', error)

      return null
    } finally {
      isLoading.value = false
    }
  }

  // 👉 Login and fetch access token // moved from backend.js
  const fetchAccessTokenByCookie = async () => {
    isLoading.value = true
    try {
      const response = await fetch(`${baseUrl}/auth/me`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${basicAuth}`,
          'Content-Type': 'application/json',
        },
        //body: JSON.stringify({ email, password }),
        credentials: 'include', // Needed to include ALL cookies with request
      })

      if (response.ok) {
        const data = await response.json()

        if (data.user) {
          setUser(data.user)
        } else {
          clearUser()
        }

        return data.access_token || null
      } else {
        console.error('Login request failed', response.status, await response.text())

        return null
      }
    } catch (error) {
      console.error('Error during login request:', error)

      return null
    } finally {
      isLoading.value = false
    }
  }

  // 👉 Get current user info and set is as currentUser
  const getCurrentUserInfo = async () => {
    //console.warn('getCurrentUserInfo')
    isLoading.value = true
    try {

      const response = await $backend(`/auth/user`, {
        method: 'GET',
      })


      if (response) {
        // Provide fallback for color property
        const userWithDefaults = {
          ...response.user,
          color: response.user?.color,
        }
        setUser(userWithDefaults)
        Sentry.setUser({
          id: String(userWithDefaults.id),
        })
        Sentry.setTag('salonerId', String(userWithDefaults.salonerId))
      } else {
        clearUser()
      }

      return response
    } catch (e) {
      console.error('Error getting message topic:', e)

      return {}
    } finally {
      isLoading.value = false
    }
  }

  // 👉 Set current user
  const setUser = (userData) => {
    currentUser.value = { ...currentUser.value, ...userData }
  }

  // 👉 Clear current user
  const clearUser = () => {
    currentUser.value = { ...blankUser.value }
  }

  // 👉 Logout user
  const logout = async () => {
    try {
      await $backend(`/auth/logout`)
      useCookie('accessToken').value = null
      clearUser()
      setTimeout(() => {
        console.warn('logout', useCookie('accessToken').value)
        window.location.href = `${import.meta.env.VITE_SALONER10_URL}login`
      }, 10)
    } catch (e) {
      //
    }
  }

  const getAmbassadorPageData = async (force = false, year = null) => {
    if (!force && ambassadorPageInfo.value.isLoaded) return
    ambassadorPageInfo.value.isLoaded = false // Reset before fetching
    isLoading.value = true
    try {
      const response = await $backend(`/ambassador/getPageData` + (year ? `?year=${year}` : ``), {
        method: 'GET',
      })
      if (response.success) {
        ambassadorPageInfo.value.isLoaded = true
        ambassadorPageInfo.value.content = response
        ambassadorPageInfo.value.isAmbassador = response.data.ambassadorProgram
        isLoading.value = false

        return true
      }
    } catch (error) {
      isLoading.value = false

      return false
    }
    isLoading.value = false

  }

  const joinAmbassadorProgram = async (regulationsAccepted) => {
    if(!regulationsAccepted) {
      showWarningToast(t('you_must_accept_regulation_to_join_ambassador_programme'))

      return
    }
    isLoading.value = true
    try {
      const response = await $backend(`/ambassador/join`, {
        method: 'POST',
        body: {
          regulationsAccepted: regulationsAccepted,
        },
      })
      if (response.success) {
        ambassadorPageInfo.value.isAmbassador = true
        showSuccessToast(response?.message)

        await getAmbassadorPageData(true) //refresh data

        return true
      } else {
        showWarningToast(response?.message)

        return false
      }
    } catch (error) {
      return false
    } finally {

      isLoading.value = false
    }
  }

  const leaveAmbassadorProgram = async () => {
    isLoading.value = true
    try {
      const response = await $backend(`/ambassador/leave`, {
        method: 'POST',
      })
      if (response.success) {
        ambassadorPageInfo.value.isAmbassador = false
        ambassadorPageInfo.value.content.data.selectedYear = (new Date()).getFullYear()
        showSuccessToast(response?.message)

        return true
      } else {
        showWarningToast(response?.message)

        return false
      }
    } catch (error) {
      return false
    } finally {
      isLoading.value = false
    }
  }

  const isLoadingDiscountCode = ref(false)
  const requestDiscountCode = async (proposalCode) => {
    isLoadingDiscountCode.value = true
    try {
      const response = await $backend(`/ambassador/proposal`, {
        method: 'POST',
        body: {
          proposalCode: proposalCode,
          // debugEmail: "magda.miller@saloner.pl", // TODO: Remove after tests
        },
      })
      if (response.success) {
        showSuccessToast(response?.message)

        return true
      } else {
        showWarningToast(response?.message)

        return false
      }
    } catch (error) {
      return false
    }
    finally {
      isLoadingDiscountCode.value = false
    }
  }


  // Send support question
  const sendSupportQuestion = async (question) => {
    isLoading.value = true
    try {
      const response = await $backend(`/support/submitContactForm`, {
        method: 'POST',
        body: { question },
      })
      if (response.success) {
        showSuccessToast(response.message)

        return response
      } else {
        return false
      }
    } catch (error) {
      return false
    } finally {
      isLoading.value = false
    }
  }

  const getSupportPageData = async (force = false) => {
    if (supportPageInfo.value.isLoaded) return
    try {
      const response = await $backend(`/support/getPageData`, {
        method: 'GET',
      })
      if (response.success) {
        supportPageInfo.value.isLoaded = true
        supportPageInfo.value.consultantPhone = response.data.consultantPhone
        supportPageInfo.value.activeSalonerProductsAlert = response.data.hasActiveLicenses === false || response.data.hasActiveLicenses === undefined

        return response
      } else {
        return false
      }
    } catch (error) {
      return false
    }
  }

  // Generate link to preview/download image
  const generateImageLink = async (fileName) => {
    try {
      const response = await $backend(`/ambassador/signedUrl?f=${fileName}`, {
        method: 'GET',
      })
      if (response.success) {
        return response.data.signedUrl
      } else {
        return false
      }
    } catch (error) {
      return false
    }
  }

  // Get statistics page data
  const getStatisticsPageData = async () => {
    if (statisticsPageInfo.value.isLoaded) return
    try {
      // const response = await $backend(`/support/getPageData`, {
      //   method: 'GET',
      // })
      // if (response.success) {
      //   statisticsPageInfo.value.isLoaded = true
      //
      //   return response
      // } else {
      //   return false
      // }
    } catch (error) {
      return false
    }
  }

  return {
    isLoading,
    currentUser,
    blankUser,
    supportPageInfo,
    ambassadorPageInfo,
    statisticsPageInfo,
    isLoadingDiscountCode,
    setUser,
    clearUser,
    fetchAccessTokenByEmail,
    fetchAccessTokenByCookie,
    getCurrentUserInfo,
    logout,
    getAmbassadorPageData,
    joinAmbassadorProgram,
    leaveAmbassadorProgram,
    requestDiscountCode,
    sendSupportQuestion,
    getSupportPageData,
    generateImageLink,
    getStatisticsPageData,
  }
})
